import { useMemo } from 'react';
import {
  CellRendererSelectorResult,
  ICellRendererParams,
} from 'ag-grid-community';
import clsx from 'clsx';

import { DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED } from './constants';

import styles from './checkboxCellRendererDecorator.module.scss';

type CheckboxProps = {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isChecked: boolean;
  align?: 'left' | 'right';
  disabled?: boolean;
  /** When true, only the checkbox is shown (no adjacent cell value text). */
  hideValue?: boolean;
};

export type CheckboxCellRendererDecoratorProps = {
  cellRendererConfig?: CellRendererSelectorResult;
} & ICellRendererParams &
  CheckboxProps;

export const CheckboxCellRendererDecorator = ({
  cellRendererConfig,
  onChange,
  isChecked,
  align,
  disabled = false,
  hideValue = false,
  ...cellRendererProps
}: CheckboxCellRendererDecoratorProps) => {
  const parent = useMemo(() => {
    if (cellRendererConfig != null) {
      return (
        <cellRendererConfig.component
          {...cellRendererConfig.params}
          {...cellRendererProps}
        />
      );
    }

    return;
  }, [cellRendererConfig, cellRendererProps]);

  const { value, valueFormatted } = cellRendererProps;

  // Interactive-only checkboxes still render when the cell value is missing.
  // Display-only usage keeps the prior empty behavior.
  if (value === undefined && !hideValue) {
    return parent;
  }

  const cellValue = valueFormatted ?? value;

  return (
    <div
      className={clsx(
        styles['root'],
        align === 'right' && styles['rootAlignRight']
      )}
      data-testid="checkbox-cell"
    >
      {/* Markup copyied over from ag-grid checkbox cell renderer */}
      <div
        {...DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED}
        className="ag-selection-checkbox"
        role="presentation"
      >
        <div
          role="presentation"
          data-ref="eCheckbox"
          className="ag-labeled ag-label-align-right ag-checkbox ag-input-field"
        >
          <div
            data-ref="eLabel"
            className="ag-input-field-label ag-label ag-hidden ag-checkbox-label"
            aria-hidden="true"
            role="presentation"
          ></div>
          <div
            data-ref="eWrapper"
            className={clsx(
              'ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper',
              disabled && 'ag-disabled'
            )}
            role="presentation"
          >
            <input
              data-ref="eInput"
              data-testid="checkbox-decorator-cell-input"
              className="ag-input-field-input ag-checkbox-input"
              type="checkbox"
              checked={isChecked}
              disabled={disabled}
              onChange={onChange}
            />
          </div>
        </div>
      </div>
      {!hideValue && (
        <span className={styles['value']}>{parent ?? cellValue}</span>
      )}
    </div>
  );
};
